import {
  campaignAnalyticsGranularitySchema,
  campaignAnalyticsKpiKeySchema,
  type ExecutionModeV1,
} from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconChevronRight,
  IconPlus,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CreateManagedGroupDialog } from "@/components/managed-group/CreateManagedGroupDialog";
import { ManagedGroupPerformancePanel } from "@/components/managed-group/ManagedGroupPerformancePanel";
import { ManagedGroupStatusBadge } from "@/components/managed-group/ManagedGroupStatusBadge";
import { PageHeader } from "@/components/patterns/page-header";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import type { PerformanceMetric } from "@/components/shared/performance/performance";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePermission } from "@/hooks/use-permission";
import { useShopScope } from "@/hooks/use-shop-scope";
import { useFormatters } from "@/lib/format";
import { fetchManagedGroupAnalytics } from "@/lib/managed-group-api";
import { requireModule } from "@/lib/module-access";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

export const Route = createFileRoute("/_authed/managed-groups")({
  beforeLoad: () => {
    requireModule("ai");
  },
  validateSearch: z.object({
    metric: campaignAnalyticsKpiKeySchema.catch("spend"),
    granularity: campaignAnalyticsGranularitySchema.catch("day"),
  }),
  component: ManagedGroupsPage,
});

type FilterValue = "all" | string;

function ManagedGroupsPage(): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const { hasPermission } = usePermission();
  const canManage = hasPermission("manage_ai");
  const { scopeShopIds } = useShopScope();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<FilterValue>("all");
  const [businessGoal, setBusinessGoal] = useState<FilterValue>("all");
  const [executionMode, setExecutionMode] = useState<FilterValue>("all");
  const [attention, setAttention] = useState<"all" | "needed" | "clear">("all");
  const query = useQuery({
    queryKey: [
      "ai-managed-groups",
      "analytics",
      scopeShopIds.join(","),
      range,
      timeZone,
      searchParams.granularity,
      search,
      status,
      businessGoal,
      executionMode,
      attention,
    ],
    queryFn: () =>
      fetchManagedGroupAnalytics(range, searchParams.granularity, timeZone, scopeShopIds, {
        search,
        status,
        businessGoal,
        executionMode,
        attention,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="flex min-h-full w-full min-w-0 flex-1 flex-col gap-[14px] px-6 pb-6 pt-[22px]">
      <PageHeader
        filters={<ReportingFilters />}
        title={t("title")}
        description={t("subtitle")}
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <IconPlus data-icon="inline-start" />
              {t("create.button")}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">{t("create.noPermission")}</span>
          )
        }
      />

      <ManagedGroupPerformancePanel
        kpis={query.data?.kpis ?? []}
        trend={query.data?.trend ?? []}
        dataThrough={query.data?.dataThrough ?? null}
        metric={searchParams.metric as PerformanceMetric}
        granularity={searchParams.granularity}
        onMetricChange={(metric) =>
          void navigate({ search: (previous) => ({ ...previous, metric }), replace: true })
        }
        onGranularityChange={(granularity) =>
          void navigate({ search: (previous) => ({ ...previous, granularity }), replace: true })
        }
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        isError={query.isError}
        onRetry={() => void query.refetch()}
      />

      <ManagedGroupFilters
        search={search}
        status={status}
        businessGoal={businessGoal}
        executionMode={executionMode}
        attention={attention}
        onSearch={setSearch}
        onStatus={setStatus}
        onBusinessGoal={setBusinessGoal}
        onExecutionMode={setExecutionMode}
        onAttention={setAttention}
      />

      {query.isLoading && !query.data ? (
        <div className="space-y-2 rounded-[14px] border border-line bg-surface p-4">
          {["one", "two", "three", "four", "five"].map((key) => (
            <Skeleton key={key} className="h-12 w-full" />
          ))}
        </div>
      ) : query.isError || !query.data ? (
        <ErrorState message={t("loadFailed")} onRetry={() => void query.refetch()} />
      ) : query.data.groups.length ? (
        <ManagedGroupTable
          groups={query.data.groups}
          nextRunAt={query.data.nextRunAt}
          aggregate={scopeShopIds.length > 1}
        />
      ) : (
        <EmptyState
          icon={<IconSparkles className="size-16" />}
          title={t("empty.title")}
          description={t("empty.description")}
          actionLabel={canManage ? t("create.button") : undefined}
          onAction={canManage ? () => setCreateOpen(true) : undefined}
        />
      )}
      <CreateManagedGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function ManagedGroupFilters({
  search,
  status,
  businessGoal,
  executionMode,
  attention,
  onSearch,
  onStatus,
  onBusinessGoal,
  onExecutionMode,
  onAttention,
}: {
  search: string;
  status: FilterValue;
  businessGoal: FilterValue;
  executionMode: FilterValue;
  attention: "all" | "needed" | "clear";
  onSearch: (value: string) => void;
  onStatus: (value: FilterValue) => void;
  onBusinessGoal: (value: FilterValue) => void;
  onExecutionMode: (value: FilterValue) => void;
  onAttention: (value: "all" | "needed" | "clear") => void;
}): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const statuses = ["all", "active", "paused_by_group", "paused_by_workspace", "archived"];
  const goals = [
    "all",
    "grow_sales",
    "improve_efficiency",
    "launch_product",
    "clear_inventory",
    "explore_and_learn",
    "defend_branded_traffic",
  ];
  const modes: Array<"all" | ExecutionModeV1> = ["all", "recommendation", "semi-auto", "full-auto"];
  const attentionValues = ["all", "needed", "clear"] as const;
  return (
    <section className="flex min-h-12 shrink-0 flex-wrap items-center gap-2 rounded-[10px] border border-line bg-surface px-3 py-2">
      <div className="relative min-w-60 flex-1 md:max-w-80">
        <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={t("filters.search")}
          className="h-8 pl-8 text-xs"
        />
      </div>
      <FilterSelect
        label={t("filters.status")}
        value={status}
        values={statuses}
        getLabel={(value) => (value === "all" ? t("filters.allStatuses") : t(`status.${value}`))}
        onChange={onStatus}
      />
      <FilterSelect
        label={t("filters.businessGoal")}
        value={businessGoal}
        values={goals}
        getLabel={(value) => (value === "all" ? t("filters.allGoals") : t(`policy.goals.${value}`))}
        onChange={onBusinessGoal}
      />
      <FilterSelect
        label={t("filters.executionMode")}
        value={executionMode}
        values={modes}
        getLabel={(value) => (value === "all" ? t("filters.allModes") : t(`policy.modes.${value}`))}
        onChange={onExecutionMode}
      />
      <FilterSelect
        label={t("filters.attention")}
        value={attention}
        values={attentionValues}
        getLabel={(value) => t(`filters.attentionValues.${value}`)}
        onChange={(value) => onAttention(value as typeof attention)}
      />
    </section>
  );
}

function FilterSelect({
  label,
  value,
  values,
  getLabel,
  onChange,
}: {
  label: string;
  value: string;
  values: readonly string[];
  getLabel: (value: string) => string;
  onChange: (value: string) => void;
}): React.ReactElement {
  const items = values.map((option) => ({ value: option, label: getLabel(option) }));
  return (
    <Select items={items} value={value} onValueChange={(next) => next && onChange(next)}>
      <SelectTrigger size="sm" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function ManagedGroupTable({
  groups,
  nextRunAt,
  aggregate,
}: {
  groups: Awaited<ReturnType<typeof fetchManagedGroupAnalytics>>["groups"];
  nextRunAt: string | null;
  aggregate: boolean;
}): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const { formatDateTime, formatPercent } = useFormatters();
  return (
    <section className="shrink-0 overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="overflow-x-auto paper-scrollbar">
        <Table className="min-w-[1120px]">
          <TableHeader>
            <TableRow>
              <TableHead>{t("columns.name")}</TableHead>
              {aggregate ? <TableHead>{t("columns.shop")}</TableHead> : null}
              <TableHead>{t("columns.status")}</TableHead>
              <TableHead className="text-right">{t("columns.members")}</TableHead>
              <TableHead className="text-right">{t("columns.targetAcos")}</TableHead>
              <TableHead className="text-right">{t("columns.actualAcos")}</TableHead>
              <TableHead>{t("columns.executionMode")}</TableHead>
              <TableHead>{t("columns.nextRun")}</TableHead>
              <TableHead>{t("columns.attention")}</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                <TableCell className="font-medium">
                  <Link
                    to="/managed-groups/$id"
                    params={{ id: group.id }}
                    className="hover:underline"
                  >
                    {group.name}
                  </Link>
                </TableCell>
                {aggregate ? (
                  <TableCell className="text-muted-foreground">{group.shopName ?? "—"}</TableCell>
                ) : null}
                <TableCell>
                  <ManagedGroupStatusBadge status={group.effectiveStatus} />
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {group.enabledMemberCount}/{group.memberCount}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {formatPercent(group.policy.targetAcos)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {group.metrics.sales > 0 ? formatPercent(group.metrics.acos) : "—"}
                </TableCell>
                <TableCell>{t(`policy.modes.${group.policy.executionMode}`)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {nextRunAt ? formatDateTime(nextRunAt) : "—"}
                </TableCell>
                <TableCell>
                  {group.needsAttention ? (
                    <Badge variant="warning">
                      <IconAlertTriangle className="size-3" />
                      {t("attention.needed", { count: group.pendingDecisions })}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t("attention.clear")}</span>
                  )}
                </TableCell>
                <TableCell>
                  <Link
                    to="/managed-groups/$id"
                    params={{ id: group.id }}
                    aria-label={t("actions.open")}
                  >
                    <IconChevronRight className="size-4 text-muted-foreground" />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
